import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { sectionBadge } from '../../utils/animationUtils';
import { PiGraduationCap, PiBuildings, PiLightning } from 'react-icons/pi';

gsap.registerPlugin(ScrollTrigger);

/*
  Neo-Brutalism rules applied:
  - Column 01 → white bg, #FFE600 accent
  - Column 02 → #111 (dark) bg, white text, #FF3366 accent
  - Column 03 → white bg, #AAFF00 accent
  - Numbers are SOLID fill color — not transparent outline
  - Thick 8px solid top bars
  - Hover = full solid accent background (not rgba tint)
  - Heavy borders, large offset shadow
*/
const educationData = [
    {
        number: "01",
        icon: <PiGraduationCap weight="bold" size={18} />,
        date: "Okt 2023 — Present",
        title: "Informatics Engineering",
        place: "Asia Cyber University",
        highlight: "Active enrollment, GPA maintained",
        // Default state
        bg: '#FFFFFF',
        textColor: '#111',
        subColor: '#888',
        numColor: '#FFE600',
        accentColor: '#FFE600',
        barBorder: '#111',
        iconBg: '#FFE600',
        // Hover state (full solid fill)
        hoverBg: '#FFE600',
        hoverText: '#111',
        hoverNum: '#111',
    },
    {
        number: "02",
        icon: <PiBuildings weight="bold" size={18} />,
        date: "Jun 2022 — Jul 2023",
        title: "Software Development",
        place: "PeTIK II Jombang",
        highlight: "Top-performing graduate cohort",
        // Default state — dark column
        bg: '#111111',
        textColor: '#FFFFFF',
        subColor: 'rgba(255,255,255,0.45)',
        numColor: '#FF3366',
        accentColor: '#FF3366',
        barBorder: '#FF3366',
        iconBg: '#FF3366',
        // Hover state
        hoverBg: '#FF3366',
        hoverText: '#FFFFFF',
        hoverNum: '#FFFFFF',
    },
    {
        number: "03",
        icon: <PiLightning weight="bold" size={18} />,
        date: "May 2019 — Apr 2022",
        title: "Electrical Engineering",
        place: "SMKN 1 Nganjuk",
        highlight: "Foundational systems & electronics",
        // Default state
        bg: '#FFFFFF',
        textColor: '#111',
        subColor: '#888',
        numColor: '#AAFF00',
        accentColor: '#AAFF00',
        barBorder: '#111',
        iconBg: '#AAFF00',
        // Hover state
        hoverBg: '#AAFF00',
        hoverText: '#111',
        hoverNum: '#111',
    },
];

const EducationSection = () => {
    const containerRef = useRef(null);
    const colRefs     = useRef([]);
    const numRefs     = useRef([]);
    const titleRefs   = useRef([]);
    const subRefs     = useRef([]);
    const ruleRefs    = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // ── Initial states ──────────────────────────────────────────
            gsap.set('.edu-badge',    { opacity: 0, scale: 0 });
            gsap.set('.edu-heading',  { clipPath: 'inset(0 100% 0 0)' });
            gsap.set('.edu-col',      { opacity: 0, y: 60 });
            gsap.set('.edu-top-rule', { scaleX: 0, transformOrigin: 'left center' });
            gsap.set('.edu-cert',     { opacity: 0, y: 40 });
            gsap.set('.edu-cert-item',{ opacity: 0, x: 20 });

            sectionBadge('.edu-badge');

            // ── Heading clip wipe ───────────────────────────────────────
            gsap.to('.edu-heading', {
                clipPath: 'inset(0 0% 0 0)',
                duration: 1, ease: 'expo.inOut',
                scrollTrigger: {
                    trigger: '.edu-heading', start: 'top 92%',
                    toggleActions: 'play none none reverse', fastScrollEnd: true,
                },
            });

            // ── Entry animation ─────────────────────────────────────────
            const st = {
                trigger: '.edu-grid', start: 'top 82%',
                toggleActions: 'play none none reverse', fastScrollEnd: true,
            };
            gsap.to('.edu-top-rule', {
                scaleX: 1, duration: 0.6, ease: 'expo.out', stagger: 0.12,
                scrollTrigger: st,
            });
            gsap.to('.edu-col', {
                opacity: 1, y: 0, stagger: 0.14, duration: 1.0, ease: 'expo.out', delay: 0.1,
                scrollTrigger: st,
            });

            // ── Accordion expand + full solid hover ─────────────────────
            const EASE = 'expo.inOut';
            const DUR  = 0.5;
            const W_EXPAND  = '46%';
            const W_SHRINK  = '27%';
            const W_DEFAULT = '33.333%';

            colRefs.current.forEach((col, i) => {
                if (!col) return;
                const edu   = educationData[i];
                const rule  = ruleRefs.current[i];
                const numEl = numRefs.current[i];

                col.addEventListener('mouseenter', () => {
                    // Expand this col with solid accent fill
                    gsap.to(col, {
                        width: W_EXPAND,
                        backgroundColor: edu.hoverBg,
                        duration: DUR, ease: EASE, overwrite: 'auto'
                    });
                    // Number → solid hoverNum color
                    gsap.to(numEl, {
                        color: edu.hoverNum,
                        opacity: 1,
                        duration: DUR * 0.8, ease: EASE, overwrite: 'auto'
                    });
                    // Text → hoverText
                    if (titleRefs.current[i]) gsap.to(titleRefs.current[i], { color: edu.hoverText, duration: DUR * 0.8, ease: EASE, overwrite: 'auto' });
                    if (subRefs.current[i]) gsap.to(subRefs.current[i], { color: edu.hoverText, duration: DUR * 0.8, ease: EASE, overwrite: 'auto' });

                    // Rule → thicker
                    gsap.to(rule, {
                        scaleY: 2.5,
                        duration: DUR * 0.6, ease: 'power3.out', overwrite: 'auto'
                    });

                    // Shrink + dim others
                    colRefs.current.forEach((otherCol, j) => {
                        if (j === i || !otherCol) return;
                        gsap.to(otherCol, {
                            width: W_SHRINK,
                            opacity: 0.35,
                            duration: DUR, ease: EASE, overwrite: 'auto'
                        });
                    });
                });

                col.addEventListener('mouseleave', () => {
                    // Restore this col to default
                    gsap.to(col, {
                        width: W_DEFAULT,
                        backgroundColor: edu.bg,
                        duration: DUR, ease: EASE, overwrite: 'auto'
                    });
                    gsap.to(numEl, {
                        color: edu.numColor,
                        opacity: 1,
                        duration: DUR, ease: EASE, overwrite: 'auto'
                    });
                    // Text → original colors
                    if (titleRefs.current[i]) gsap.to(titleRefs.current[i], { color: edu.textColor, duration: DUR, ease: EASE, overwrite: 'auto' });
                    if (subRefs.current[i]) gsap.to(subRefs.current[i], { color: edu.subColor, duration: DUR, ease: EASE, overwrite: 'auto' });

                    gsap.to(rule, {
                        scaleY: 1,
                        duration: DUR * 1.2, ease: 'elastic.out(1, 0.6)', overwrite: 'auto'
                    });

                    // Restore others
                    colRefs.current.forEach((otherCol, j) => {
                        if (j === i || !otherCol) return;
                        const otherEdu = educationData[j];
                        gsap.to(otherCol, {
                            width: W_DEFAULT,
                            backgroundColor: otherEdu.bg,
                            opacity: 1,
                            duration: DUR, ease: EASE, overwrite: 'auto'
                        });
                    });
                });
            });

            // ── Certifications ──────────────────────────────────────────
            gsap.to('.edu-cert', {
                opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
                scrollTrigger: { trigger: '.edu-cert', start: 'top 92%', toggleActions: 'play none none reverse', fastScrollEnd: true },
            });
            gsap.to('.edu-cert-item', {
                opacity: 1, x: 0, stagger: 0.1, duration: 0.6, ease: 'expo.out',
                scrollTrigger: { trigger: '.edu-cert', start: 'top 86%', toggleActions: 'play none none reverse', fastScrollEnd: true },
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="education"
            ref={containerRef}
            className="py-24 font-jakarta bg-[#F2EFE9]"
            style={{ overflow: 'hidden' }}
        >
            <div className="max-w-7xl mx-auto px-6">

                {/* Header */}
                <div className="mb-16">
                    <div
                        className="edu-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full mb-5"
                        style={{
                            boxShadow: '3px 3px 0 #111',
                            fontFamily: "'Space Mono', monospace",
                            fontSize: 10, fontWeight: 700,
                            letterSpacing: '0.2em', textTransform: 'uppercase',
                        }}
                    >
                        <span className="w-2 h-2 rounded-full bg-[#AAFF00] inline-block" />
                        EDUCATION
                    </div>
                    <h2
                        className="edu-heading"
                        style={{
                            fontFamily: "'Syne', sans-serif",
                            fontWeight: 800, letterSpacing: '-0.03em',
                            lineHeight: 1.05, fontSize: 'var(--text-h1)', color: 'var(--ink)',
                        }}
                    >
                        Academic<br />
                        <span style={{ color: 'transparent', WebkitTextStroke: '2px #111' }}>Background.</span>
                    </h2>
                </div>

                {/* Outer shell — carries border + shadow only */}
                <div
                    style={{
                        border: '3px solid #111',
                        borderRadius: 14,
                        boxShadow: '8px 8px 0 #111',
                        marginBottom: 72,
                        overflow: 'visible',
                    }}
                >
                    {/* Inner flex grid */}
                    <div
                        className="edu-grid"
                        style={{
                            display: 'flex',
                            alignItems: 'stretch',
                            borderRadius: 12,
                            overflow: 'hidden',
                            minHeight: 340,
                        }}
                    >
                        {educationData.map((edu, i) => (
                            <div
                                key={i}
                                ref={el => colRefs.current[i] = el}
                                className="edu-col"
                                style={{
                                    position: 'relative',
                                    width: '33.333%',
                                    flexShrink: 0,
                                    borderRight: i < 2 ? '3px solid #111' : 'none',
                                    padding: '0 0 28px',
                                    cursor: 'default',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: edu.bg,
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Top accent bar — thick, solid */}
                                <div
                                    ref={el => ruleRefs.current[i] = el}
                                    className="edu-top-rule"
                                    style={{
                                        height: 8,
                                        backgroundColor: edu.accentColor,
                                        borderBottom: '2px solid #111',
                                        marginBottom: 20,
                                        transformOrigin: 'left center',
                                        flexShrink: 0,
                                    }}
                                />

                                {/* Content */}
                                <div style={{ padding: '0 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>

                                    {/* Big solid number — NOT outline, fully filled */}
                                    <span
                                        ref={el => numRefs.current[i] = el}
                                        className="edu-outline-num"
                                        style={{
                                            fontFamily: "'Space Mono', monospace",
                                            fontWeight: 700,
                                            fontSize: 'clamp(52px, 5.5vw, 80px)',
                                            color: edu.numColor,
                                            lineHeight: 1,
                                            display: 'block',
                                            marginBottom: 18,
                                            userSelect: 'none',
                                            letterSpacing: '-0.04em',
                                        }}
                                    >
                                        {edu.number}
                                    </span>

                                    {/* Institution pill — sharp corners, solid bg */}
                                    <div
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 7,
                                            marginBottom: 14,
                                            alignSelf: 'flex-start',
                                            background: edu.iconBg,
                                            border: `2px solid ${edu.textColor === '#FFFFFF' ? edu.accentColor : '#111'}`,
                                            borderRadius: 4,
                                            padding: '4px 10px 4px 6px',
                                        }}
                                    >
                                        <span style={{ color: edu.textColor === '#FFFFFF' ? '#111' : '#111', display: 'flex', alignItems: 'center' }}>
                                            {edu.icon}
                                        </span>
                                        <span
                                            style={{
                                                fontFamily: "'Space Mono', monospace",
                                                fontSize: 9, fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: '0.1em',
                                                color: '#111',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {edu.place}
                                        </span>
                                    </div>

                                    {/* Thick divider line */}
                                    <div
                                        style={{
                                            height: 2,
                                            backgroundColor: edu.textColor === '#FFFFFF'
                                                ? 'rgba(255,255,255,0.15)'
                                                : 'rgba(17,17,17,0.15)',
                                            marginBottom: 14,
                                        }}
                                    />

                                    {/* Degree title */}
                                    <h3
                                        ref={el => titleRefs.current[i] = el}
                                        style={{
                                            fontFamily: "'Syne', sans-serif",
                                            fontWeight: 800,
                                            fontSize: 'clamp(16px, 1.6vw, 22px)',
                                            color: edu.textColor,
                                            lineHeight: 1.15,
                                            letterSpacing: '-0.02em',
                                            marginBottom: 10,
                                            flex: 1,
                                        }}
                                    >
                                        {edu.title}
                                    </h3>

                                    {/* Highlight italic */}
                                    <p
                                        ref={el => subRefs.current[i] = el}
                                        style={{
                                            fontFamily: "'DM Sans', sans-serif",
                                            fontSize: 12, lineHeight: 1.5,
                                            color: edu.subColor,
                                            marginBottom: 16,
                                            fontStyle: 'italic',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {edu.highlight}
                                    </p>

                                    {/* Date badge — solid, sharp */}
                                    <div
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0,
                                            alignSelf: 'flex-start',
                                        }}
                                    >
                                        <span
                                            style={{
                                                backgroundColor: '#111',
                                                color: edu.accentColor,
                                                fontFamily: "'Space Mono', monospace",
                                                fontSize: 9, fontWeight: 700,
                                                letterSpacing: '0.1em', textTransform: 'uppercase',
                                                padding: '4px 10px',
                                                borderRadius: 3,
                                                border: `1.5px solid ${edu.textColor === '#FFFFFF' ? 'rgba(255,255,255,0.2)' : '#111'}`,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {edu.date}
                                        </span>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Certifications */}
                {/* <div className="edu-cert">
                    <div
                        style={{
                            backgroundColor: '#111',
                            border: '3px solid #111',
                            borderRadius: 14,
                            padding: 32,
                            boxShadow: '6px 6px 0 #FFE600',
                        }}
                    >
                        <h3
                            style={{
                                fontFamily: "'Syne', sans-serif",
                                fontWeight: 700, fontSize: 'var(--text-h3)',
                                letterSpacing: '-0.02em', color: 'white', marginBottom: 24,
                            }}
                        >
                            Certifications <span style={{ color: '#FFE600' }}>&</span> Credentials
                        </h3>
                        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                "Microsoft Certified: Azure Fundamentals",
                                "Dicoding: Belajar Dasar Pemrograman Web",
                                "FreeCodeCamp: Responsive Web Design",
                            ].map((cert, index) => (
                                <li
                                    key={index}
                                    className="edu-cert-item flex items-start gap-3 p-4"
                                    style={{
                                        border: '2px solid #FFE600',
                                        boxShadow: '3px 3px 0 #FFE600',
                                        borderRadius: 6, backgroundColor: 'transparent',
                                    }}
                                >
                                    <span style={{ color: '#FFE600', flexShrink: 0, marginTop: 2 }}>◆</span>
                                    <span
                                        style={{
                                            fontFamily: "'DM Sans', sans-serif",
                                            fontWeight: 500, fontSize: 'var(--text-sm)',
                                            color: '#ccc', lineHeight: 1.6,
                                        }}
                                    >
                                        {cert}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div> */}

            </div>
        </section>
    );
};

export default EducationSection;